'use client'

import { useState, useCallback } from 'react'
import { motion } from 'framer-motion'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { LogIn, Mail, Lock, Sparkles, TrendingUp, Users, Zap } from 'lucide-react'
import AnimatedEye from './AnimatedEye'
import HmnCharacter from './HmnCharacter'
import type { CharacterState } from './HmnCharacter'
import Image from 'next/image'
import { useBranding } from '@/lib/contexts/BrandingContext'

export default function LoginPage() {
  const { branding } = useBranding()
  const searchParams = useSearchParams()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [characterState, setCharacterState] = useState<CharacterState>('idle')
  const [focusedField, setFocusedField] = useState<'email' | 'password' | null>(null)

  const handleFieldFocus = useCallback((field: 'email' | 'password') => {
    setFocusedField(field)
    setCharacterState(field)
  }, [])

  const handleFieldBlur = useCallback((blurringField: 'email' | 'password') => {
    // Small delay to check if focus moved to the other field
    setTimeout(() => {
      setFocusedField(prev => {
        // If focus already moved to the other field, don't reset
        if (prev !== null && prev !== blurringField) return prev
        setCharacterState('idle')
        return null
      })
    }, 100)
  }, [])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)
    setCharacterState('loading')

    try {
      // Clear any stale auth data before attempting login
      if (typeof window !== 'undefined') {
        const authKey = 'sb-egqqdscvxvtwcdwknbnt-auth-token'
        const existingAuth = localStorage.getItem(authKey)
        if (existingAuth) {
          localStorage.removeItem(authKey)
        }
      }

      // Pass returnUrl through so the server can honour the original destination
      const returnUrl = searchParams?.get('returnUrl') || searchParams?.get('return_url') || undefined

      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, ...(returnUrl ? { returnUrl } : {}) }),
      })

      const data = await response.json()

      if (!data.success) {
        throw new Error(data.error?.message || 'Login failed')
      }

      setCharacterState('success')
      // Redirect based on user role - use window.location to ensure cookies are set
      window.location.href = data.data.redirectPath
    } catch (err: any) {
      setError(err.message || 'Login failed. Please try again.')
      setCharacterState('error')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen hg-bg-primary flex">
      {/* Left Side - Information Panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ background: 'linear-gradient(to bottom right, var(--hg-cyan-bg), var(--hg-info-bg), var(--hg-cyan-bg))' }}>
        {/* Animated Background */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(139,92,246,0.15),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,rgba(59,130,246,0.1),transparent_50%)]" />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-16 py-20">
          {/* Logo */}
          <Link href="/" className="mb-12 inline-block">
            <Image
              src={branding?.logo?.url || '/hmn_logo.png'}
              alt={branding?.company_name || 'HMN'}
              width={200}
              height={50}
              className="h-10 w-auto"
              priority
            />
          </Link>

          {/* Main Heading */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl font-bold hg-text-primary mb-6 font-gendy leading-tight">
              Welcome Back to Your AI Transformation Journey
            </h1>
            <p className="text-xl hg-text-secondary mb-12 font-diatype leading-relaxed">
              Access your personalized dashboard and continue building AI maturity across your organization.
            </p>
          </motion.div>

          {/* Features Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-2 gap-6"
          >
            <div className="bg-[var(--hg-bg-card)] backdrop-blur-xl rounded-xl p-6 border border-[var(--hg-border-color)]">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: 'var(--hg-cyan-bg)' }}>
                <TrendingUp className="w-6 h-6" style={{ color: 'var(--hg-cyan-text)' }} />
              </div>
              <h3 className="hg-text-primary font-semibold mb-2 font-gendy">Track Progress</h3>
              <p className="hg-text-muted text-sm font-diatype">Monitor your AI maturity level and growth</p>
            </div>

            <div className="bg-[var(--hg-bg-card)] backdrop-blur-xl rounded-xl p-6 border border-[var(--hg-border-color)]">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: 'var(--hg-info-bg)' }}>
                <Zap className="w-6 h-6" style={{ color: 'var(--hg-info)' }} />
              </div>
              <h3 className="hg-text-primary font-semibold mb-2 font-gendy">AI Workflows</h3>
              <p className="hg-text-muted text-sm font-diatype">Deploy ready-to-use automation</p>
            </div>

            <div className="bg-[var(--hg-bg-card)] backdrop-blur-xl rounded-xl p-6 border border-[var(--hg-border-color)]">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: 'var(--hg-cyan-bg)' }}>
                <Users className="w-6 h-6" style={{ color: 'var(--hg-cyan-text)' }} />
              </div>
              <h3 className="hg-text-primary font-semibold mb-2 font-gendy">Expert Network</h3>
              <p className="hg-text-muted text-sm font-diatype">Connect with AI transformation leaders</p>
            </div>

            <div className="bg-[var(--hg-bg-card)] backdrop-blur-xl rounded-xl p-6 border border-[var(--hg-border-color)]">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: 'var(--hg-success-bg)' }}>
                <Sparkles className="w-6 h-6" style={{ color: 'var(--hg-success)' }} />
              </div>
              <h3 className="hg-text-primary font-semibold mb-2 font-gendy">Live Workshops</h3>
              <p className="hg-text-muted text-sm font-diatype">Interactive sessions with experts</p>
            </div>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-12 pt-8 border-t border-[var(--hg-border-color)]"
          >
            <div className="grid grid-cols-3 gap-8">
              <div>
                <div className="text-3xl font-bold hg-text-primary mb-1 font-gendy">500+</div>
                <div className="text-sm hg-text-muted font-diatype">Organizations</div>
              </div>
              <div>
                <div className="text-3xl font-bold hg-text-primary mb-1 font-gendy">10K+</div>
                <div className="text-sm hg-text-muted font-diatype">Users Trained</div>
              </div>
              <div>
                <div className="text-3xl font-bold hg-text-primary mb-1 font-gendy">95%</div>
                <div className="text-sm hg-text-muted font-diatype">Satisfaction</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 sm:px-12 lg:px-16 py-12">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          {/* Mobile Logo */}
          <Link href="/" className="lg:hidden mb-6 inline-block">
            <Image
              src={branding?.logo?.url || '/hmn_logo.png'}
              alt={branding?.company_name || 'HMN'}
              width={180}
              height={50}
              className="h-10 w-auto"
            />
          </Link>

          {/* Animated Character */}
          <HmnCharacter
            formState={characterState}
            passwordVisible={showPassword}
            emailValue={email}
          />

          {/* Header */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold hg-text-primary mb-2 font-gendy">Sign In</h2>
            <p className="hg-text-muted font-diatype">Access your transformation dashboard</p>
          </div>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 rounded-xl text-sm font-diatype"
              style={{ backgroundColor: 'var(--hg-danger-bg, rgba(239,68,68,0.1))', border: '1px solid var(--hg-danger-border, rgba(239,68,68,0.2))', color: 'var(--hg-danger)' }}
            >
              {error}
            </motion.div>
          )}

          {/* Form */}
          <form onSubmit={handleLogin} className="space-y-5">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium hg-text-secondary mb-2 font-diatype">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 hg-text-muted" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => handleFieldFocus('email')}
                  onBlur={() => handleFieldBlur('email')}
                  placeholder="definitely.not.ai@company.com"
                  required
                  autoComplete="email"
                  className="w-full pl-12 pr-4 py-3.5 bg-[var(--hg-bg-hover)] border border-[var(--hg-border-color)] rounded-xl hg-text-primary placeholder-[var(--hg-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--hg-cyan-500)] focus:border-[var(--hg-cyan-500)] transition-all font-diatype"
                  style={{ fontSize: '16px' }}
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium hg-text-secondary mb-2 font-diatype">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 hg-text-muted" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => handleFieldFocus('password')}
                  onBlur={() => handleFieldBlur('password')}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                  className="w-full pl-12 pr-12 py-3.5 bg-[var(--hg-bg-hover)] border border-[var(--hg-border-color)] rounded-xl hg-text-primary placeholder-[var(--hg-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--hg-cyan-500)] focus:border-[var(--hg-cyan-500)] transition-all font-diatype"
                  style={{ fontSize: '16px' }}
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <AnimatedEye
                    isOpen={!showPassword}
                    onToggle={() => {
                      setShowPassword(prev => !prev)
                      // Keep character in password state so cover/peek triggers
                      setCharacterState('password')
                      setFocusedField('password')
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Remember & Forgot */}
            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 hg-text-muted cursor-pointer">
                <input
                  type="checkbox"
                  className="rounded border-[var(--hg-border-color)] bg-[var(--hg-bg-hover)] focus:ring-[var(--hg-cyan-500)]"
                  style={{ color: 'var(--hg-cyan-500)' }}
                />
                <span className="font-diatype">Remember me</span>
              </label>
              <Link href="/reset-password" className="font-diatype" style={{ color: 'var(--hg-cyan-text)' }}>
                Forgot password?
              </Link>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-4 btn-primary font-semibold rounded-xl flex items-center justify-center gap-2 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed font-diatype"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 rounded-full animate-spin" style={{ borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'var(--hg-text-inverse, #fff)' }} />
                  Signing in...
                </>
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  Sign In
                </>
              )}
            </button>
          </form>

          {/* Sign Up Link */}
          <div className="mt-8 text-center">
            <p className="hg-text-muted text-sm font-diatype">
              Don't have an account?{' '}
              <Link href="/signup" className="font-semibold" style={{ color: 'var(--hg-cyan-text)' }}>
                Create one now →
              </Link>
            </p>
          </div>

          {/* Back to Home */}
          <div className="mt-6 text-center">
            <Link
              href="/"
              className="inline-flex items-center gap-2 text-sm hg-text-muted transition-colors font-diatype"
            >
              ← Back to Home
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  )
}
